import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Loading, TextInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetPlatformFeeCardSecretMutation, useLazyGetDebitsDetailsQuery } from "@/redux/apis/payment/stripe/stripeApi";
import { useAppSelector } from "@/redux/hooks";
import { stripeSelector } from "@/redux/slices/payment/selectors";
import { addToast } from "@/utils";
import { cardElementOptions } from "../fixtures";

/**
 * Platform-fee card on file, for countries with no direct-debit rail (Switzerland: Stripe has no CHF
 * direct debit). Same lifecycle as the debit editors: a SetupIntent from the backend, confirmed here
 * with the card element, stored by the setup_intent.succeeded webhook, read back via the debit
 * details endpoint.
 */
export const PlatformFeeCardEditor = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [getDebitsDetails] = useLazyGetDebitsDetailsQuery();
  const [getCardSecret, { isLoading }] = useGetPlatformFeeCardSecretMutation();
  const {
    debits: { clientSecret },
  } = useAppSelector(stripeSelector);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getCardSecret()
      .unwrap()
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    const card = elements.getElement(CardElement);
    if (!card || !fullName || !email) return;

    setIsSubmitting(true);
    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card,
        billing_details: { name: fullName, email },
      },
    });

    if (result.error) {
      addToast("danger", result.error.message || "Something went wrong. Please contact your administrator.");
      setIsSubmitting(false);
      return;
    }

    // The webhook persists the card; give it a moment before re-reading, as the debit editors do.
    setTimeout(() => {
      getDebitsDetails();
      addToast("success", "Successfully saved your platform fee card");
      setTimeout(() => window.location.replace("../"), 1250);
    }, 8000);
  };

  return (
    <form id="stripe-platform-fee-card-form" className="relative flex flex-col" onSubmit={handleSubmit}>
      {(isLoading || isSubmitting) && <Loading />}
      <div className="flex flex-col gap-y-3 rounded-lg bg-neutral-surface-gray p-5 lg:w-[573px]">
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Direct debit is not available in your country. Platform fees are collected from the card below.
        </Typography>
        <div className="flex flex-col gap-y-1">
          <Typography variant="paragraph">Card</Typography>
          <CardElement options={cardElementOptions} />
        </div>
        <div className="flex flex-col gap-y-1">
          <Typography variant="paragraph">Email</Typography>
          <TextInput
            placeholder="john.smith@example.com"
            size="md"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <Typography variant="paragraph">Cardholder name</Typography>
          <TextInput
            placeholder="John Smith"
            size="md"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>
        <Typography variant="small" className="text-neutral-dark-gray">
          By clicking &quot;Save card&quot;, you authorise Rideminder to charge this card for platform fees as they fall due. You can
          replace the card at any time from this page.
        </Typography>
      </div>
      <div className="mt-4 flex flex-row gap-x-4">
        <Button variant="primary" type="submit" form="stripe-platform-fee-card-form">
          Save card
        </Button>
        <Button variant="secondary" onClick={() => navigate("../")}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
